// Jadwal kirim bertahap (2026-10-11). Mode: langsung | bertahap tiap N menit | acak dalam rentang jam.
// Semua jam dihitung sebagai jam dinding WIB tanpa zona ("YYYY-MM-DDTHH:mm") — kolom scheduled_at
// di server bertipe timestamp tanpa zona, jadi string ISO ber-"Z" akan meleset 7 jam.
// Acak memakai seed: pratinjau = yang dikirim; jadwal hanya berubah saat "Acak ulang".
import React, { useEffect, useMemo, useState } from 'react';
import { colors, radius } from '../theme';

const pad = n => String(n).padStart(2, '0');
// Jam dinding WIB sekarang sebagai "YYYY-MM-DDTHH:mm".
function nowWib(addMin = 0) {
  const d = new Date(Date.now() + 7 * 3600e3 + addMin * 60e3);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}
// Aritmetika menit pada jam dinding tanpa zona (diperlakukan sebagai UTC semu, lalu diformat balik).
function addMinutes(naive, min) {
  const [d, t] = naive.split('T'); const [y, m, dd] = d.split('-').map(Number); const [hh, mm] = t.split(':').map(Number);
  const x = new Date(Date.UTC(y, m - 1, dd, hh, mm) + Math.round(min) * 60e3);
  return `${x.getUTCFullYear()}-${pad(x.getUTCMonth() + 1)}-${pad(x.getUTCDate())}T${pad(x.getUTCHours())}:${pad(x.getUTCMinutes())}`;
}
const label = naive => { const [d, t] = naive.split('T'); const [, m, dd] = d.split('-'); return `${dd}/${m} ${t}`; };
// Jadwal relatif (2026-10-12): rencana dihitung dari jam dasar semu lalu diubah jadi selisih menit.
const BASIS = '2000-01-01T00:00';
const selisih = naive => Math.round((Date.parse(naive + ':00Z') - Date.parse(BASIS + ':00Z')) / 60000);
const labelRel = m => (m >= 60 ? `+${Math.floor(m / 60)} j ${m % 60} mnt` : `+${m} mnt`);
function rng(seed) { let a = seed >>> 0; return () => { a |= 0; a = (a + 0x6D2B79F5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }; }

/** Rencana jadwal: [{channel_id, channel_name, scheduled_at}] atau [] (mode langsung). */
export function buatRencana({ mode, start, gap, hours, acakUrutan, seed }, channels) {
  if (mode === 'langsung' || !channels.length || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(start || '')) return [];
  const r = rng(seed || 1);
  let urut = [...channels];
  if (mode === 'acak' || acakUrutan) for (let i = urut.length - 1; i > 0; i--) { const j = Math.floor(r() * (i + 1)); [urut[i], urut[j]] = [urut[j], urut[i]]; }
  if (mode === 'bertahap') {
    const g = Math.max(1, Number(gap) || 1);
    return urut.map((c, i) => ({ channel_id: c.id, channel_name: c.channel_name, scheduled_at: addMinutes(start, i * g) }));
  }
  // Acak dalam rentang: menit acak di [0, rentang], diurutkan, jarak minimal 1 menit bila muat.
  const span = Math.max(1, Math.round((Number(hours) || 1) * 60));
  let mins = urut.map(() => Math.floor(r() * (span + 1))).sort((a, b) => a - b);
  if (span >= urut.length - 1) for (let i = 1; i < mins.length; i++) if (mins[i] <= mins[i - 1]) mins[i] = mins[i - 1] + 1;
  if (mins.length && mins[mins.length - 1] > span) { const over = mins[mins.length - 1] - span; mins = mins.map(m => Math.max(0, m - over)); }
  return urut.map((c, i) => ({ channel_id: c.id, channel_name: c.channel_name, scheduled_at: addMinutes(start, mins[i]) }))
    .sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at));
}

/** relatifLabel: teks acuan jadwal relatif, mis. "setelah render selesai" (Shorts) / "setelah klik Setujui" (UGC). */
export default function JadwalKirim({ channels, onChange, relatifLabel = 'setelah render selesai' }) {
  const [mode, setMode] = useState('langsung');
  const [acuan, setAcuan] = useState('jam'); // 'jam' = jam tertentu | 'relatif' = N menit setelah …
  const [nAwal, setNAwal] = useState(5);
  const [start, setStart] = useState(() => nowWib(10));
  const [gap, setGap] = useState(5);
  const [hours, setHours] = useState(4);
  const [acakUrutan, setAcakUrutan] = useState(false);
  const [seed, setSeed] = useState(() => Math.floor(Math.random() * 1e9));
  const [lihat, setLihat] = useState(false);
  const ids = channels.map(c => c.id).join(',');
  const rencana = useMemo(() => {
    if (acuan !== 'relatif') return buatRencana({ mode, start, gap, hours, acakUrutan, seed }, channels);
    // Relatif: susun dari jam dasar semu, lalu kirim selisih menit (+N awal) — worker/server yang
    // mengubahnya jadi jam sungguhan saat render selesai / saat Setujui.
    const n = Math.max(0, Math.min(1440, Math.round(Number(nAwal) || 0)));
    return buatRencana({ mode, start: BASIS, gap, hours, acakUrutan, seed }, channels)
      .map(r => ({ channel_id: r.channel_id, channel_name: r.channel_name, offset_min: selisih(r.scheduled_at) + n }));
  }, [mode, start, gap, hours, acakUrutan, seed, ids, acuan, nAwal]); // eslint-disable-line
  useEffect(() => { onChange && onChange(rencana); }, [rencana]); // eslint-disable-line
  const lewat = mode !== 'langsung' && acuan === 'jam' && start && start < nowWib(0);
  const tampil = r => (r.offset_min != null ? labelRel(r.offset_min) : label(r.scheduled_at));

  const opsi = [
    { id: 'langsung', label: 'Langsung' },
    { id: 'bertahap', label: 'Bertahap tiap N menit' },
    { id: 'acak', label: 'Acak dalam rentang jam' },
  ];
  const input = { padding: '7px 10px', fontSize: 13, borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bgCard, color: colors.textPrimary, boxSizing: 'border-box' };
  return (
    <div style={{ marginBottom: 14, padding: '12px 14px', background: colors.bgCardMuted, borderRadius: 10, border: `1px solid ${colors.border}` }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <i className="ti ti-clock-play" style={{ fontSize: 16 }}></i>Jadwal kirim ke channel YouTube
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: mode === 'langsung' ? 0 : 10 }}>
        {opsi.map(o => (
          <button key={o.id} type="button" onClick={() => setMode(o.id)}
            style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600, borderRadius: 999, cursor: 'pointer',
              border: `1.5px solid ${mode === o.id ? colors.accent : colors.borderStrong}`, background: mode === o.id ? colors.accentMuted : colors.bgCard,
              color: mode === o.id ? colors.accentText : colors.textPrimary }}>{o.label}</button>
        ))}
      </div>
      {mode !== 'langsung' && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
            <div style={{ fontSize: 12, color: colors.textSecondary, display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 230px' }}>
              <span>Mulai</span>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[{ id: 'jam', l: 'Jam tertentu' }, { id: 'relatif', l: `N menit ${relatifLabel}` }].map(o => (
                  <button key={o.id} type="button" onClick={() => setAcuan(o.id)}
                    style={{ padding: '4px 10px', fontSize: 11.5, fontWeight: 600, borderRadius: 999, cursor: 'pointer',
                      border: `1px solid ${acuan === o.id ? colors.accent : colors.border}`, background: acuan === o.id ? colors.accentMuted : colors.bgCard,
                      color: acuan === o.id ? colors.accentText : colors.textSecondary }}>{o.l}</button>
                ))}
              </div>
              {acuan === 'jam' ? (
                <input type="datetime-local" value={start} onChange={e => setStart(e.target.value)} style={input} />
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="number" min="0" max="1440" value={nAwal} onChange={e => setNAwal(e.target.value)} style={{ ...input, width: 90 }} />
                  <span>menit {relatifLabel}</span>
                </span>
              )}
            </div>
            {mode === 'bertahap' ? (
              <label style={{ fontSize: 12, color: colors.textSecondary, display: 'flex', flexDirection: 'column', gap: 4, flex: '0 1 140px' }}>
                Jeda tiap (menit)
                <input type="number" min="1" max="1440" value={gap} onChange={e => setGap(e.target.value)} style={input} />
              </label>
            ) : (
              <label style={{ fontSize: 12, color: colors.textSecondary, display: 'flex', flexDirection: 'column', gap: 4, flex: '0 1 140px' }}>
                Dalam rentang (jam)
                <input type="number" min="0.5" max="72" step="0.5" value={hours} onChange={e => setHours(e.target.value)} style={input} />
              </label>
            )}
            {mode === 'acak' ? (
              <button type="button" onClick={() => setSeed(Math.floor(Math.random() * 1e9))} className="btn btn-outline" style={{ fontSize: 12 }}>🎲 Acak ulang</button>
            ) : (
              <label style={{ fontSize: 12, color: colors.textSecondary, display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 8 }}>
                <input type="checkbox" checked={acakUrutan} onChange={e => setAcakUrutan(e.target.checked)} style={{ width: 'auto' }} /> Acak urutan channel
              </label>
            )}
          </div>
          {rencana.length > 0 && (
            <div style={{ marginTop: 10, fontSize: 12, color: colors.textSecondary }}>
              <b style={{ color: colors.textPrimary }}>{rencana.length} channel</b> dijadwalkan {tampil(rencana[0])} – {tampil(rencana[rencana.length - 1])}{acuan === 'relatif' ? ` ${relatifLabel}` : ' WIB'}.{' '}
              <span onClick={() => setLihat(v => !v)} style={{ color: colors.accentText, cursor: 'pointer', fontWeight: 600 }}>{lihat ? 'Tutup daftar' : 'Lihat jadwal per channel'}</span>
              {lihat && (
                <div style={{ marginTop: 8, maxHeight: 220, overflowY: 'auto', background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.md }}>
                  {rencana.map(r => (
                    <div key={r.channel_id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '6px 10px', borderBottom: `1px solid ${colors.border}` }}>
                      <span style={{ color: colors.textPrimary, overflowWrap: 'anywhere' }}>{r.channel_name}</span>
                      <span style={{ whiteSpace: 'nowrap' }}>{tampil(r)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {lewat && <div style={{ marginTop: 6, fontSize: 11, color: colors.warning }}>Waktu mulai sudah lewat — channel dengan jam yang sudah lewat langsung diproses.</div>}
          <div style={{ marginTop: 6, fontSize: 11, color: colors.textSecondary }}>
            Jadwal ini menggantikan jam per channel yang diisi manual. Bila job juga ke TikTok/Instagram, keduanya diposting setelah semua channel YouTube selesai.
          </div>
        </>
      )}
    </div>
  );
}
